/// <amd-module name="Repos/FilesHubCommon/Components/FileExplorerTree" />
import * as React from "react";
import { IFolderContents, IGitItem } from "Repos/Files/Contracts";
import * as Resources from "Repos/FilesHubCommon/Resources";
import { IObservableArrayEventArgs, ObservableCollection, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Icon } from "VSSUI/Icon";
import { Intersection } from "VSSUI/Intersection";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { ListSelection } from "VSSUI/List";
import { IMenuItem, MoreButton } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { ColumnMore, ColumnMoreWidths, ITableColumn } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ExpandableTreeCell, ITreeColumn, ITreeRow, Tree } from "VSSUI/TreeEx";
import { ITreeItem, ITreeItemEx, ITreeItemProvider, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { IFilesHubCommonService } from "../Contracts";
import { FileDropTarget } from "./FileDropTarget";


function getTreeColumns(filesService: IFilesHubCommonService, webEditingEnabled: boolean): ITreeColumn<IGitItem>[] {
    return [
        {
            hierarchical: true,
            id: "name",
            name: Resources.Name,
            renderCell: (rowIndex: number, columnIndex: number, treeColumn: ITableColumn<ITreeItemEx<IGitItem>>, treeItem: ITreeItemEx<IGitItem>) => {
                const item = treeItem.underlyingItem.data;

                let content = (
                    <>
                        <Icon {...item.iconProps} className={css("icon-margin medium", item.iconProps.className)} />
                        
                        <FPSLink
                            href={item.href}
                            className="bolt-table-link"
                            onClick={
                                (event) => {
                                    event.preventDefault();
                                    filesService.setSelectedItem(item);
                                }
                            }
                            removeUnderline={true}
                        >
                            <Tooltip text={item.path}>
                                <span className="text-ellipsis">{item.name}</span>
                            </Tooltip>
                        </FPSLink>
                    </>
                );

                if (item.isFolder) {
                    content = (
                        <FileDropTarget
                            className="flex-grow flex-row text-ellipsis"
                            onDrop={dataTransfer => filesService.dropFiles(item, dataTransfer)}
                        >
                            {content}
                        </FileDropTarget>
                    );
                }
                return (
                    <ExpandableTreeCell
                        columnIndex={columnIndex}
                        contentClassName="repos-file-explorer-tree-cell"
                        key={columnIndex}
                        treeColumn={treeColumn}
                        treeItem={treeItem}
                    >
                        {content}
                    </ExpandableTreeCell>
                );
            },
            width: -100
        },
        new ColumnMore(
            item => {
                return {
                    id: "tree-sub-menu",
                    items: filesService.getItemMenuItems(item.underlyingItem.data, [
                        "ms.vss-code-web.source-item-menu",
                        "ms.vss-code-web.source-tree-item-menu"
                    ]),
                    webEditingEnabled
                };
            },
            undefined,
            undefined,
            ColumnMoreWidths.compact
        )
    ];
}

export interface IFileExplorerTreeProps {
    filesService: IFilesHubCommonService;
    webEditingEnabled?: ObservableValue<boolean>;
}

export class FileExplorerTree extends VssComponent<IFileExplorerTreeProps> {
    private itemProvider: ITreeItemProvider<IGitItem>;
    private itemsByPath: { [path: string]: ITreeItem<IGitItem> };
    private rootItem: IGitItem;
    private selection = new ListSelection({ multiSelect: false, selectOnFocus: false });
    private treeRef = React.createRef<Tree<IGitItem>>();

    constructor(props: IFileExplorerTreeProps, context: IVssComponentContext) {
        super(props, context);

        this.rootItem = props.filesService.getRootItem();
        const loadedFolders = props.filesService.getLoadedFolders();

        this.itemsByPath = {};
        let rootItems: ITreeItem<IGitItem>[] = [];

        for (const folder of loadedFolders.value) {
            const parentTreeItem = this.itemsByPath[folder.path];

            if (parentTreeItem || folder.path === this.rootItem.path) {
                const treeItems = this.createTreeItemsForFolder(folder);
                if (parentTreeItem) {
                    parentTreeItem.expanded = true;
                    parentTreeItem.childItems = treeItems;
                } else {
                    rootItems = treeItems;
                }
            }
        }

        this.itemProvider = new TreeItemProvider<IGitItem>(rootItems);

        loadedFolders.subscribe(this.onFolderLoaded);
        this.addUnmountCallback(() => loadedFolders.unsubscribe(this.onFolderLoaded));
    }

    public componentDidMount() {
        super.componentDidMount();

        const selectedItem = this.props.filesService.getSelectedItem();
        this.setSelectedItem(selectedItem.value);
        selectedItem.subscribe(this.setSelectedItem);
        this.addUnmountCallback(() => selectedItem.unsubscribe(this.setSelectedItem));
    }

    public render() {
        const filesService = this.props.filesService;
        const webEditingEnabled = this.props.webEditingEnabled ?? new ObservableValue(true);

        return (
            <Intersection>
                <div className="flex-grow flex-column v-scroll-auto custom-scrollbar scroll-auto-hide">
                    <div className="repos-file-explorer-header flex-row body-xl">
                        <Icon {...this.rootItem.iconProps} className="icon-margin font-size-ml repos-git-icon" />
                        <FileDropTarget
                            className="flex-row flex-grow scroll-hidden"
                            onDrop={dataTransfer => filesService.dropFiles(filesService.getRootItem(), dataTransfer)}
                        >
                            <Tooltip overflowOnly={true}>
                                <a
                                    className="repos-file-explorer-header-repo-link text-ellipsis"
                                    role="button"
                                    href={this.rootItem.href}
                                    onClick={this.onRootFolderClick}
                                >
                                    {this.rootItem.name}
                                </a>
                            </Tooltip>
                        </FileDropTarget>
                        <Observer webEditingEnabled={webEditingEnabled}>
                            {() => {
                                return (
                                    <MoreButton
                                        className="repos-file-explorer-header-menu"
                                        contextualMenuProps={{
                                            menuProps: {
                                                id: "header-submenu",
                                                items: this.getMenuItems(filesService, webEditingEnabled.value)
                                            }
                                        }}
                                    />
                                );
                            }}
                        </Observer>
                    </div>
                    <Tree<IGitItem>
                        ariaLabel={Resources.FileExplorerTree}
                        className="repos-file-explorer-tree"
                        columns={getTreeColumns(filesService, webEditingEnabled.value)}
                        itemProvider={this.itemProvider}
                        onActivate={this.onActivate}
                        onToggle={this.onToggle}
                        ref={this.treeRef}
                        selectableText={true}
                        selection={this.selection}
                        showHeader={false}
                        showLines={false}
                    />
                </div>
            </Intersection>
        );
    }

    private getMenuItems(filesService: IFilesHubCommonService, webEditingEnabled: boolean): ObservableCollection<IMenuItem> {
        const menuItems = new ObservableCollection<IMenuItem>();
        menuItems.push(filesService.getCloneMenuItems());
        menuItems.push(filesService.getItemMenuItems(filesService.getRootItem(), undefined, webEditingEnabled));
        menuItems.push([
            {
                id: "hide-explorer",
                onActivate: () => filesService.setExplorerCollapsed(true),
                text: Resources.HideExplorerPane
            }
        ]);
        return menuItems;
    }

    private setSelectedItem = (selectedItem: IGitItem): void => {
        const items = this.itemProvider.value;
        for (let i = 0, l = items.length; i < l; i++) {
            if (selectedItem.path === this.itemProvider.value[i].underlyingItem.data.path) {
                this.selection.select(i);
                if (this.treeRef.current) {
                    this.treeRef.current.scrollIntoView(i, {
                        block: "nearest"
                    });
                }
                return;
            }
        }

        // Item not found, clear any existing selection
        this.selection.clear();
    };

    private onToggle = (event: React.SyntheticEvent<HTMLElement>, treeItem: ITreeItemEx<IGitItem>) => {
        this.itemProvider.toggle(treeItem.underlyingItem);

        if (treeItem.underlyingItem.expanded) {
            this.props.filesService.getChildItems(treeItem.underlyingItem.data.path);
        }
    };

    private createTreeItemsForFolder(folder: IFolderContents): ITreeItem<IGitItem>[] {
        const results: ITreeItem<IGitItem>[] = [];

        for (const item of folder.children) {
            const treeItem: ITreeItem<IGitItem> = {
                data: item
            };
            if (item.isFolder) {
                // Populate a single loading row to show a shimmer while fetching children
                treeItem.childItems = [{ data: new ObservableValue(undefined) } as any];
            }

            this.itemsByPath[item.path] = treeItem;
            results.push(treeItem);
        }

        return results;
    }

    private onFolderLoaded = (value: IObservableArrayEventArgs<IFolderContents>) => {
        if (value.addedItems) {
            for (const folder of value.addedItems) {
                const parentTreeItem = this.itemsByPath[folder.path];
                if (parentTreeItem) {
                    parentTreeItem.expanded = true;
                }

                if (parentTreeItem || folder.path === this.rootItem.path) {
                    const treeItems = this.createTreeItemsForFolder(folder);
                    this.itemProvider.splice(parentTreeItem, parentTreeItem ? parentTreeItem.childItems : [], [{ items: treeItems }]);
                }
            }
            if (this.selection.selectedCount === 0) {
                this.setSelectedItem(this.props.filesService.getSelectedItem().value);
            }
        }
    };

    private onRootFolderClick = (event: React.SyntheticEvent<HTMLElement>) => {
        this.props.filesService.setSelectedPath(this.rootItem.path);
        event.preventDefault();
    };

    private onActivate = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITreeRow<IGitItem>): void => {
        if (ObservableLike.getValue(tableRow.data.underlyingItem.data)) {
            this.props.filesService.setSelectedItem(tableRow.data.underlyingItem.data);
            event.preventDefault();
        }
    };
}
